if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          mains: this.querySelectorAll(".product-media-main"),
          thumbnails: this.querySelectorAll(
            ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
          ),
          slideshow: this.querySelector(".product-media-main-wrapper"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        // v6 gallery templates (2026-10-29, PDP INP): outside the theme editor the photos the selected option does not
        // show ship inside <template data-cq-photo-tpl> (slides) and <template data-cq-thumb-tpl> (thumbnails) at the
        // end of their containers (snippets/product-media-gallery.liquid, snippets/product-media-thumbnails.liquid), so
        // the page builds, styles and lays out only the shown ones (the whole house RO carried 48 hidden slides and 48
        // hidden thumbnails that every style recalculation walked). materialize() moves what a later state needs into
        // the live DOM. Same approach as the phone menu panels (snippets/cq-nav-mobile.liquid, cq-header.js mount()).
        this.templated = Boolean(this.querySelector("template[data-cq-photo-tpl], template[data-cq-thumb-tpl]"));
        this.elements.thumbnails.forEach((thumbnail) => this.wireThumbnail(thumbnail));
      }

      // One thumbnail's controls, for the ones live at start up and for each one materialize() brings in later. Once
      // per button, so a thumbnail never gets a second click handler.
      wireThumbnail(thumbnail) {
        const mediaToggle = thumbnail.querySelector("button");
        if (!mediaToggle || mediaToggle.cqGalleryWired) {
          return;
        }
        mediaToggle.cqGalleryWired = true;
        mediaToggle.removeAttribute("aria-haspopup");
        mediaToggle.setAttribute("aria-current", thumbnail.classList.contains("is-active") ? "true" : "false");
        mediaToggle.addEventListener(
          "click",
          this.setActiveMedia.bind(this, mediaToggle.dataset.mediaId, false),
        );
        // AJ 2026-10-18 03:37 ("when i click on cartridge and switch pictures its blank grey then the image loads"):
        // reaching for a thumbnail (hover, focus or finger down) starts its large photo loading before the click.
        const reach = () => this.prime(this.mediaElement(this.elements.mains, mediaToggle.dataset.mediaId), "high");
        ["pointerenter", "focus", "pointerdown"].forEach((type) => mediaToggle.addEventListener(type, reach, { passive: true }));
      }

      // The templates' nodes that `ids` names (every one when ids is omitted) join the live DOM, each in its printed
      // place: after the shown photos, among the hidden ones in Shopify's order (data-cq-pos). The place is read from
      // the live nodes of the same kind, not from the template, because Flickity moves the slides into its own slider
      // and back (deactivate appends them after the template). The nodes move, nothing is cloned, so every attribute
      // (alt, srcset, sizes, loading, data, aria) is the printed one, and the custom elements inside (deferred-media,
      // modal-opener) upgrade as they land. Hidden nodes keep cq-photo-off: CQVariantPhotos.arrange() decides what
      // shows. Returns the nodes moved in. Other scripts call this before they query the gallery
      // (assets/cq-photo-viewer.js build(), assets/sticky-cart.js syncThumb()).
      materialize(ids) {
        const moved = [];
        if (!this.templated) return moved;
        const wanted = ids == null ? null : new Set([...ids].map(String));
        let left = false;
        [
          ["template[data-cq-photo-tpl]", ".product-media-main"],
          ["template[data-cq-thumb-tpl]", ".product-media-thumbnail"],
        ].forEach(([templateSelector, selector]) => {
          const template = this.querySelector(templateSelector);
          if (!template) return;
          const stored = [...template.content.querySelectorAll(selector)];
          const picks = wanted ? stored.filter((node) => wanted.has(String(node.dataset.mediaId))) : stored;
          picks.forEach((node) => {
            const live = [...this.querySelectorAll(selector)];
            const last = live[live.length - 1];
            // The container that holds the live nodes now: the slideshow, Flickity's slider or the rail track.
            const container = last ? last.parentElement : template.parentElement;
            const pos = Number(node.dataset.cqPos);
            const next = live.find((element) => element.parentElement === container
              && element.classList.contains("cq-photo-off") && Number(element.dataset.cqPos) > pos);
            if (next) container.insertBefore(node, next);
            else if (last) last.after(node);
            else container.insertBefore(node, template.parentElement === container ? template : null);
            moved.push(node);
          });
          if (template.content.querySelector(selector)) left = true;
          else template.remove();
        });
        this.templated = left;
        if (!moved.length) return moved;
        this.elements.mains = this.querySelectorAll(".product-media-main");
        this.elements.thumbnails = this.querySelectorAll(
          ".product-media-thumbnail:not(.product-media-thumbnail-variant), .product-media-thumbnail:first-child",
        );
        moved.forEach((node) => {
          if (node.matches(".product-media-thumbnail")) this.wireThumbnail(node);
        });
        return moved;
      }

      // The photos the current plan shows that still sit in a template: none in the normal flow (applyVariantPhotos
      // materializes before it arranges), so the carousel build and the rail's scroll can ask cheaply before they read.
      materializeNeeded() {
        if (!this.templated || !this.photoKey) return [];
        return this.materialize(this.photoKey.split(","));
      }

      // Every slide, live or still in its template: the planner (CQVariantPhotos.plan) needs each photo's id, position
      // and group to decide what an option shows, and the preloads need the hidden photos' files.
      allMains() {
        const template = this.templated ? this.querySelector("template[data-cq-photo-tpl]") : null;
        return [...this.elements.mains, ...(template ? template.content.querySelectorAll(".product-media-main") : [])];
      }

      allThumbnails() {
        const template = this.templated ? this.querySelector("template[data-cq-thumb-tpl]") : null;
        return [...this.elements.thumbnails, ...(template ? template.content.querySelectorAll(".product-media-thumbnail") : [])];
      }

      connectedCallback() {
        this.setupStickyGallery();
        this.productPage = this.closest(".cq-product-page");
        if (this.productPage && !this.variantMediaHandler) {
          this.variantMediaHandler = (event) => {
            const variant = event.detail?.variant;
            if (this.showVariantPhotos(variant)) return;
            if (variant?.mediaId) this.setActiveMedia(variant.mediaId, false, true);
          };
          this.productPage.addEventListener("cq:variant-change", this.variantMediaHandler);
          // P1 (AJ 2026-10-11, "the images load quite slowly"): reaching for an option (finger down, focus or a key)
          // starts loading the photos its other values show, so the change that follows is usually instant.
          this.intentEvents = new AbortController();
          const warm = (event) => {
            const group = event.target.closest?.("cq-purchase-options [data-cq-option]");
            if (group) this.afterPhotoPaint("intent", () => this.warmOptionPhotos(group));
          };
          ["pointerdown", "focusin", "keydown"].forEach((type) => {
            this.productPage.addEventListener(type, warm, { capture: true, passive: true, signal: this.intentEvents.signal });
          });
        }
        this.photoKey = this.shownPhotoIds().join(",");
        this.schedulePhotoPreload();
        // v5.7: the photos beyond one change away wait for the first reach into the option groups (pointer over a
        // card, a finger down or focus), then load at low priority after the next painted frame. Once.
        if (this.productPage && !this.restIntent) {
          this.restIntent = new AbortController();
          const rest = (event) => {
            if (!event.target.closest?.("cq-purchase-options [data-cq-option]")) return;
            this.restIntent?.abort();
            this.restIntent = null;
            this.afterPhotoPaint("rest", () => this.warmVariantPhotos("rest"));
          };
          ["pointerover", "touchstart", "focusin"].forEach((type) => {
            this.productPage.addEventListener(type, rest, { capture: true, passive: true, signal: this.restIntent.signal });
          });
        }
        // v6 gallery templates: the thumbnail rail's scroll asks for the current plan's thumbnails still in their
        // template (a no-op after applyVariantPhotos, cheap), and the zoom modal's open (product-modal.js dispatches
        // product:modal-open) asks for every photo, as its content lists them all. Nothing here runs on a page whose
        // photos are all live (the editor, a product that shows every photo).
        if (!this.templateEvents && this.templated) {
          this.templateEvents = new AbortController();
          const options = { passive: true, signal: this.templateEvents.signal };
          const track = this.querySelector("[data-cq-rail-track], .product-media-thumbnails");
          track?.addEventListener("scroll", () => {
            if (this.railFrame != null) return;
            this.railFrame = requestAnimationFrame(() => {
              this.railFrame = null;
              this.materializeNeeded();
            });
          }, options);
          document.addEventListener("product:modal-open", () => this.materialize(), options);
        }
        if (this.elements.slideshow && !this.photoResizeObserver && "ResizeObserver" in window) {
          this.sizedImages = this.sizedImages || new Map();
          this.photoResizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            const widthChanged = width !== this.photoWidth;
            this.photoWidth = width;
            if (this.productPage) {
              this.afterPhotoPaint("resize", () => {
                this.sizedImages.forEach((size, image) => {
                  // The photo viewer raises sizes on hover for zoom. Keep that larger source.
                  if (image.sizes === size) this.sizePhoto(image);
                });
              });
            }
            // The carousel re-measures after the paint, and only when the slideshow's width changes. A phone's
            // toolbar sliding in or out changes the window height, not this width, so it costs nothing here.
            if (widthChanged && this.flickity) this.afterPhotoPaint("carousel", () => this.syncCarousel());
          });
          this.photoResizeObserver.observe(this.elements.slideshow);
        }

        // The phone carousel is built after the first paint, on a laid-out page, instead of inside this callback
        // while the deferred scripts are still writing to the document. Until then the stylesheet shows the active
        // photo on its own (section-main-product.css), in the same place and at the same size.
        if (!this.carouselQuery) {
          // Mirrors section-main-product.css: a carousel below 750 px, a single photo from there.
          this.carouselQuery = window.matchMedia("(min-width: 750px)");
          this.carouselQueryHandler = () => this.afterPhotoPaint("carousel", () => this.syncCarousel());
          if (this.carouselQuery.addEventListener) this.carouselQuery.addEventListener("change", this.carouselQueryHandler);
          else this.carouselQuery.addListener?.(this.carouselQueryHandler);
        }
        this.afterPhotoPaint("carousel", () => this.syncCarousel());
      }

      // One carousel for the life of the gallery: created where the stylesheet shows one, switched off and back on
      // when the viewport crosses the breakpoint (a large phone turned sideways), re-measured when its width changes.
      // Nothing here destroys it (see applyVariantPhotos).
      syncCarousel() {
        if (!this.isConnected || !this.elements.slideshow || !this.carouselQuery) return;
        const wanted = !this.carouselQuery.matches;
        const flickity = this.flickity;
        if (!flickity) {
          if (wanted && window.Flickity) this.createFlickity();
          return;
        }
        if (!wanted) {
          flickity.deactivate();
          return;
        }
        if (flickity.isActive) flickity.resize();
        else {
          // v6: every slide the plan shows is live before Flickity collects its cells again (a no-op in the normal flow).
          this.materializeNeeded();
          flickity.activate();
        }
      }

      createFlickity() {
        // v6: every slide the plan shows is live before Flickity collects its cells (a no-op in the normal flow).
        this.materializeNeeded();
        this.flickity = new Flickity(this.elements.slideshow, {
          autoPlay: false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          // Size changes and the breakpoint are handled by syncCarousel (one ResizeObserver and one media query),
          // so Flickity neither listens to window resizes nor probes the stylesheet on each of them.
          resize: false,
          draggable: true,
          prevNextButtons: false,
          // r7: photos the selected option does not show are not slides.
          cellSelector: ".product-media:not(.cq-photo-off)",
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          adaptiveHeight: !this.productPage,
          watchCSS: false,
          on: {
            change: (index) => {
              const { element } = this.flickity.cells[index];
              const mediaId = element.dataset.mediaId;
              this.setActiveMedia(mediaId, false, this.changingVariant === true);
              this.afterPhotoPaint("neighbours", () => this.warmNeighbours());
            },
          },
        });
        // P1 (AJ 2026-10-11: "i dont like how easy it is to accidentally scroll side ways on product pages because of the
        // product image"): Flickity moved the photo after 3 px sideways, so a scroll that started a little crooked
        // slid it. The photo now follows only a finger that clearly travels sideways: more than 30 px across and at least
        // twice as far across as up or down. The direction is settled after the first 10 px, so a scroll that later
        // drifts sideways never starts a swipe.
        // AJ 2026-10-21 ("when trying to scroll through pictures with swipe on mobile, it's too easy to accidentally
        // scroll down"): Flickity listens for touchmove on window, which browsers treat as passive, so its
        // preventDefault never held the page still, and touch-action: pan-y let any vertical drift scroll the page
        // while the photo moved. Now the direction is settled once per gesture after 10 px: sideways when the finger
        // has gone at least 0.7 times as far across as up or down. A sideways gesture holds the page still (our own
        // non-passive touchmove on the viewport) and moves the photo; any other gesture scrolls the page and leaves
        // the photo alone. Two fingers are a pinch: never blocked, never a swipe. Taps still open the photo viewer.
        const flickity = this.flickity;
        let axis = "";
        const settle = (dx, dy) => {
          const x = Math.abs(dx);
          const y = Math.abs(dy);
          if (!axis && Math.max(x, y) >= 10) axis = x >= 0.7 * y ? "x" : "y";
          return x >= 0.7 * y;
        };
        flickity.on("pointerDown", () => {
          if (axis !== "pinch") axis = "";
          this.warmNeighbours();
        });
        flickity.hasDragStarted = (move) => {
          settle(move.x, move.y);
          return axis === "x";
        };
        // Pinch-zoom stays available on the photo (Flickity's default pan-y turns it off).
        flickity._touchActionValue = "pan-y pinch-zoom";
        flickity.viewport.style.touchAction = flickity._touchActionValue;
        let start = null;
        const viewport = flickity.viewport;
        viewport.addEventListener(
          "touchstart",
          (event) => {
            if (event.touches.length > 1) {
              axis = "pinch";
              start = null;
              return;
            }
            axis = "";
            start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
          },
          { passive: true },
        );
        viewport.addEventListener(
          "touchmove",
          (event) => {
            if (!start || axis === "pinch" || event.touches.length > 1 || !event.cancelable) return;
            const touch = event.touches[0];
            const sideways = settle(touch.clientX - start.x, touch.clientY - start.y);
            // Before the direction is settled a sideways-leaning move already holds the page, so the page never
            // starts scrolling under a swipe (iOS decides on the first touchmove).
            if (axis === "x" || (!axis && sideways)) event.preventDefault();
          },
          { passive: false },
        );
        // Phone slides hidden from screen readers also leave the keyboard order (shared helper in scripts.js).
        window.cqCarouselInert?.(this.elements.slideshow, ".product-media");
        // A photo chosen before the carousel existed (within the first frame) is the slide it starts on. The change
        // handler finds that photo already active and leaves the thumbnails and the rail alone.
        const index = flickity.cells.findIndex((cell) => cell.element.classList.contains("is-active"));
        if (index > 0) {
          const changing = this.changingVariant;
          this.changingVariant = true;
          try { flickity.select(index, false, true); }
          finally { this.changingVariant = changing; }
        }
      }

      // v5.4 A12: keep the first photo's request clear; other variant leads start only after load, at idle.
      schedulePhotoPreload() {
        if (this.photoLoadHandler || this.photoIdleTask != null) return;
        this.photoLoadHandler = () => {
          this.photoLoadHandler = null;
          const warm = () => {
            this.photoIdleTask = null;
            if (this.isConnected) this.warmVariantPhotos();
          };
          this.photoIdleTask = window.requestIdleCallback
            ? window.requestIdleCallback(warm, { timeout: 2000 })
            : window.setTimeout(warm, 2000);
        };
        if (document.readyState === "complete") this.photoLoadHandler();
        else window.addEventListener("load", this.photoLoadHandler, { once: true });
      }

      // v5.7: at idle after load only the photos one change away from the current choice are primed (the other
      // values of each option group, in group order, deduplicated, at most six, each at the rendered tile width);
      // "rest" (the first reach into the option groups, see connectedCallback) primes the other variants' lead
      // photos, up to twelve more. Every photo is primed once.
      warmVariantPhotos(scope = "adjacent") {
        const picker = this.productPage?.querySelector("cq-purchase-options");
        const variants = picker?.variants;
        // v6: the hidden photos still in their template count too; prime() fetches those without materializing them.
        const mains = this.allMains();
        if (!Array.isArray(variants)) {
          if (scope !== "adjacent") return;
          const at = Math.max(0, mains.findIndex((element) => element.classList.contains("is-active")));
          [1, 2, -1].forEach((step) => this.prime(mains[(at + step + mains.length) % mains.length], "low"));
          return;
        }
        const photos = window.CQVariantPhotos;
        const mode = this.dataset.cqPhotos;
        const planned = photos && mode && (mode !== "all" || this.dataset.cqPhotoOrder);
        const items = planned ? mains.map((element) => photos.read(element)) : [];
        const activeId = mains.find((element) => element.classList.contains("is-active"))?.dataset.mediaId;
        const primed = (this.primedPhotoIds ||= new Set());
        const limit = scope === "adjacent" ? 6 : 12;
        const candidates = scope === "adjacent"
          ? this.adjacentVariants(picker)
          : [...this.adjacentVariants(picker), ...variants.filter((variant) => String(variant.id) !== String(picker.variant?.id))];
        let count = 0;
        for (const variant of candidates) {
          const id = planned
            ? photos.plan(items, mode, variant.mediaId, photos.pick?.(this, variant), this.dataset.cqPhotoOrder)[0]
            : variant.mediaId;
          if (!id || String(id) === activeId || primed.has(String(id))) continue;
          const main = this.mediaElement(mains, id);
          const image = main?.querySelector("img");
          if (!image) continue;
          primed.add(String(id));
          this.prime(main, "low");
          if (image.isConnected) image.decode?.().catch(() => {});
          if (++count === limit) break;
        }
      }

      // The variants one change away from the current choice: for each option group, each other value with the
      // shopper's other choices kept (the exact match, else the first in stock, else the first), group by group.
      adjacentVariants(picker) {
        const variants = picker?.variants;
        const current = picker?.variant;
        if (!Array.isArray(variants) || !Array.isArray(current?.options)) return [];
        const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
        const out = [];
        current.options.forEach((chosen, index) => {
          const seen = new Set();
          variants.forEach((candidate) => {
            const value = candidate.options?.[index];
            const key = String(value ?? "").trim().toLowerCase();
            if (value == null || same(value, chosen) || seen.has(key)) return;
            seen.add(key);
            const matches = variants.filter((item) => same(item.options?.[index], value));
            const keeps = (item) => item.options.every((other, i) => i === index || same(other, current.options[i]));
            const variant = matches.find(keeps) || matches.find((item) => item.available) || matches[0];
            if (variant) out.push(variant);
          });
        });
        return out;
      }

      // Hidden slides cannot measure themselves. The visible tile supplies sizes so the browser selects the
      // matching srcset candidate (including device pixel ratio), instead of the old wider column estimate.
      sizePhoto(image, width = this.photoWidth) {
        if (!this.productPage || !image?.srcset) return;
        // The observer reports the tile width after layout; until then (first paint) measure once.
        if (!(width > 0)) width = this.elements.slideshow?.getBoundingClientRect().width;
        if (!(width > 0)) return;
        const previous = this.sizedImages?.get(image);
        if (previous && image.sizes !== previous) return;
        const size = `${Math.ceil(width)}px`;
        if (image.sizes !== size) image.sizes = size;
        (this.sizedImages ||= new Map()).set(image, size);
      }

      // The slides next to the shown one load ahead (low priority), so a swipe never lands on an empty tile.
      warmNeighbours() {
        const flickity = this.flickity;
        if (!flickity?.isActive || flickity.cells.length < 2) return;
        const count = flickity.cells.length;
        [1, -1].forEach((step) => {
          this.prime(flickity.cells[(flickity.selectedIndex + step + count) % count]?.element, "low");
        });
      }

      // Starts loading a slide's photo now: the file its srcset and sizes pick, at the given priority.
      prime(element, priority, deferSizing = false) {
        const image = element?.querySelector("img");
        if (deferSizing) this.afterPhotoPaint("size", () => this.sizePhoto(image));
        else this.sizePhoto(image);
        if (!image || (image.complete && image.naturalWidth)) return;
        if (priority === "high" || image.fetchPriority !== "high") image.fetchPriority = priority;
        image.decoding = "async";
        image.loading = "eager";
        // v6 gallery templates: a slide still inside its template has no live <img>, so nothing above starts a
        // request. Its file is fetched into the cache by a detached image with the same candidates (srcset and the
        // sizes just written), the way cq-header.js warms the desktop panels still in their templates; when the slide
        // is materialized its own eager <img>, with the same sizes, is a cache hit and the tile opens drawn.
        if (!image.isConnected) this.probe(image, priority);
      }

      probe(image, priority) {
        const probes = (this.probes ||= new WeakSet());
        if (probes.has(image)) return;
        probes.add(image);
        const warm = new Image();
        if (image.sizes) warm.sizes = image.sizes;
        if ("fetchPriority" in warm) warm.fetchPriority = priority;
        if (image.srcset) warm.srcset = image.srcset;
        warm.src = image.getAttribute("src") || "";
        warm.decode?.().catch(() => {});
      }

      // v5.5: geometry, preloading and rail bookkeeping must not hold up the selected photo's first paint.
      // A keyed queue keeps only the latest selection during rapid taps and is cancelled on disconnect.
      afterPhotoPaint(key, work) {
        (this.photoTasks ||= new Map()).set(key, work);
        if (this.photoFrame != null || this.photoTimer != null) return;
        this.photoFrame = requestAnimationFrame(() => {
          this.photoFrame = null;
          this.photoTimer = setTimeout(() => {
            this.photoTimer = null;
            const tasks = [...this.photoTasks.values()];
            this.photoTasks.clear();
            if (this.isConnected) tasks.forEach((task) => task());
          }, 0);
        });
      }

      mediaElement(list, id) {
        return [...list].find((element) => element.dataset.mediaId === String(id));
      }

      // P1: the photos the other values of an option group lead with, given the shopper's other choices (the same
      // variant the picker would choose for an exact match, else the first one in stock). Every screen loads their
      // small thumbnails, which also stand in while a large photo is still arriving, and the main tile's own
      // responsive file at low priority. At most six photos per group, only after shopper intent.
      warmOptionPhotos(group) {
        const picker = group.closest("cq-purchase-options");
        const variants = picker?.variants;
        const current = picker?.variant;
        if (!Array.isArray(variants) || !current?.options) return;
        const index = Number(group.dataset.cqOption);
        const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();
        const ids = new Set();
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input.disabled) return;
          const matches = variants.filter((variant) => same(variant.options?.[index], input.value));
          const keeps = (variant) => variant.options.every((value, i) => i === index || same(value, current.options[i]));
          const variant = matches.find(keeps) || matches.find((item) => item.available) || matches[0];
          if (variant?.mediaId) ids.add(String(variant.mediaId));
        });
        const mains = this.allMains();
        const thumbnails = this.allThumbnails();
        [...ids].slice(0, 6).forEach((id) => {
          const thumbnail = this.mediaElement(thumbnails, id)?.querySelector("img");
          if (thumbnail?.loading === "lazy") thumbnail.loading = "eager";
          // v6: a thumbnail still in its template is fetched like its slide (probe), so it can stand in later.
          if (thumbnail && !thumbnail.isConnected) this.probe(thumbnail, "low");
          this.prime(this.mediaElement(mains, id), "low");
        });
      }

      // P1: an option's photo shown before its file has arrived has its thumbnail (small, loaded first, see
      // warmOptionPhotos) behind it, so the tile is never empty; the photo covers it once loaded.
      standIn(main) {
        const image = main?.querySelector("img");
        const box = image?.parentElement;
        const thumbnail = this.mediaElement(this.elements.thumbnails, main?.dataset.mediaId)?.querySelector("img");
        if (!box || !thumbnail || (image.complete && image.naturalWidth)) return;
        const show = () => {
          if (image.complete && image.naturalWidth) return;
          box.style.setProperty("--cq-stand-in", `url("${thumbnail.currentSrc.replace(/"/g, "%22")}")`);
          box.toggleAttribute("data-cq-stand-in", true);
        };
        if (thumbnail.complete && thumbnail.naturalWidth) show();
        else thumbnail.addEventListener("load", show, { once: true });
        ["load", "error"].forEach((type) => image.addEventListener(type, () => box.removeAttribute("data-cq-stand-in"), { once: true }));
      }

      shownPhotoIds() {
        return [...this.elements.mains]
          .filter((element) => !element.classList.contains("cq-photo-off"))
          .map((element) => element.dataset.mediaId);
      }

      // r7 (AJ 2026-10-08): an option change shows that option's photos in the main image, the thumbnail
      // row and the zoom modal, without a fetch (rules in assets/cq-option-picker.js, CQVariantPhotos).
      // Returns false when the product shows every photo, so the caller just moves to the option's photo.
      showVariantPhotos(variant) {
        const photos = window.CQVariantPhotos;
        const mode = this.dataset.cqPhotos;
        if (!photos || !mode || (mode === "all" && !this.dataset.cqPhotoOrder)) return false;
        // v6: the plan reads every photo, the hidden ones still in their template included.
        const mains = this.allMains();
        const ids = photos.plan(mains.map((element) => photos.read(element)), mode, variant?.mediaId, photos.pick?.(this, variant), this.dataset.cqPhotoOrder);
        if (!ids.length) return false;
        const key = ids.join(",");
        if (key === this.photoKey) {
          this.setActiveMedia(ids[0], false, true);
          return true;
        }
        // v5.4 A12: switch immediately (0 ms), with the existing thumbnail stand-in if a cold request is still
        // arriving. Waiting on ready() added up to 400 ms even after the shopper's choice had changed.
        this.afterPhotoPaint("thumbnails", () => {
          const wanted = new Set(ids);
          this.elements.thumbnails.forEach((thumbnail) => {
            if (!wanted.has(thumbnail.dataset.mediaId)) return;
            thumbnail.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = "eager"; });
          });
        });
        this.changingVariant = true;
        try { this.applyVariantPhotos(ids); }
        finally { this.changingVariant = false; }
        return true;
      }

      applyVariantPhotos(ids) {
        const photos = window.CQVariantPhotos;
        // P1 (AJ 2026-10-11 on his iPhone: after an option change the photo "just straight up doesnt load.. cant change
        // image, cant click on empty space and open it, just blank ... unless you refresh"): this used to destroy the
        // carousel and build a new one. Flickity 2.3's destroy() leaves its debounced resize running, and an iPhone
        // resizes the window whenever its toolbar shows or hides; within 150 ms of the tap the old carousel woke up
        // (watchCSS), took every slide into a second, hidden viewport and left the visible one empty, for good.
        // Now the one carousel keeps running: the slides are re-ordered inside it and it re-reads its cells.
        // v6 gallery templates: the photos this option shows that still sit in their templates join the live DOM first
        // (hidden, in their printed place), so arrange() finds every planned slide now and, in the details task below,
        // every planned thumbnail. Only the planned ones move in; the rest stay out of the page.
        this.materialize(ids);
        const flickity = this.flickity?.isActive ? this.flickity : null;
        photos.arrange(flickity ? flickity.slider : this.elements.slideshow, [...this.elements.mains], ids);
        if (flickity) {
          // reloadCells() would first put the old wrap-around copies back at their old places, over the new layout.
          flickity.beforeShiftCells = [];
          flickity.afterShiftCells = [];
          flickity.selectedIndex = 0;
          flickity.reloadCells();
          flickity.select(0, false, true);
        }
        this.afterPhotoPaint("details", () => {
          const track = this.querySelector(".product-media-thumbnails");
          photos.arrange(track, [...this.elements.thumbnails], ids);
          const modal = document.getElementById(this.id.replace("MediaGallery-", "ProductModal-"));
          const modalContent = modal?.querySelector(".product-media-modal-content");
          if (modalContent) photos.arrange(modalContent, [...modalContent.querySelectorAll(":scope > [data-cq-pos]")], ids);

          // Screen reader labels count the photos this option shows. Written only when the count moved.
          const label = (element, template, index) => {
            const text = element?.querySelector(".product-media-toggle .visually-hidden");
            if (!text || !template) return;
            const value = template.replace("__N__", index + 1);
            if (text.textContent !== value) text.textContent = value;
          };
          ids.forEach((id, index) => {
            label(this.mediaElement(this.elements.thumbnails, id), this.dataset.cqLoadLabel, index);
            // r10 item 12: an image's button is "Zoom image N"; video and 3D keep the modal wording.
            const main = this.mediaElement(this.elements.mains, id);
            label(main, main?.querySelector(".product-modal-opener-image") ? this.dataset.cqZoomLabel || this.dataset.cqOpenLabel : this.dataset.cqOpenLabel, index);
          });

          // The rail's scroll reset and the reads behind the reveal wait one more frame, so the moves above are laid
          // out by the frame itself instead of a forced layout inside this task (setActiveMedia leaves the reveal to
          // this task while the photos change).
          this.afterPhotoPaint("rail", () => {
            if (track) track.scrollLeft = 0;
            this.querySelector("cq-thumb-rail")?.schedule?.();
            const thumbnail = this.mediaElement(this.elements.thumbnails, ids[0]);
            if (thumbnail?.classList.contains("is-active")) this.revealThumbnail(thumbnail);
          });
        });
        this.photoKey = ids.join(",");
        this.setActiveMedia(ids[0], false, true);
        this.standIn(this.mediaElement(this.elements.mains, ids[0]));
        this.afterPhotoPaint("neighbours", () => this.warmNeighbours());
      }

      setupStickyGallery() {
        if (!this.closest(".cq-product-page")) return;
        this.galleryWrap = this.closest(".product-media-gallery-wrap");
        if (!this.galleryWrap || this.stickyGalleryHandler) return;
        this.galleryTop = null;
        this.gallerySticky = null;
        // A7 4e: the gallery pins only from 1024 px; narrower screens do no work per scroll frame.
        this.stickyGalleryQuery = window.matchMedia("(min-width: 1024px)");

        this.stickyGalleryHandler = () => {
          if (this.stickyGalleryFrame || (!this.stickyGalleryQuery.matches && !this.gallerySticky)) return;
          this.stickyGalleryFrame = requestAnimationFrame(() => {
            this.stickyGalleryFrame = null;
            this.updateStickyGallery();
          });
        };
        window.addEventListener("scroll", this.stickyGalleryHandler, { passive: true });
        window.addEventListener("resize", this.stickyGalleryHandler);
        this.stickyGalleryObserver = new ResizeObserver(this.stickyGalleryHandler);
        this.stickyGalleryObserver.observe(this.galleryWrap);
        const pinnedWrapper = this.galleryWrap.querySelector(".product-media-wrapper");
        if (pinnedWrapper) this.stickyGalleryObserver.observe(pinnedWrapper);
        this.galleryHeader = document.querySelector(".cq-header-bar, .section-header");
        if (this.galleryHeader) {
          this.stickyGalleryObserver.observe(this.galleryHeader);
          this.galleryHeaderObserver = new MutationObserver(this.stickyGalleryHandler);
          this.galleryHeaderObserver.observe(this.galleryHeader, { attributes: true, attributeFilter: ["class"] });
          this.galleryHeader.addEventListener("transitionend", this.stickyGalleryHandler);
        }
        // In a frame: a call here forced a layout while the page was still parsing.
        this.stickyGalleryHandler();
      }

      updateStickyGallery() {
        // Below 1024 px: undo a pin left from a wider window, before any read.
        if (!this.stickyGalleryQuery.matches) {
          if (this.gallerySticky) this.galleryWrap.classList.remove("cq-gallery-sticky");
          this.gallerySticky = false;
          return;
        }
        const bar = document.querySelector(".cq-header-bar");
        const legacy = document.querySelector(".section-header");
        const legacyEnabled = legacy?.matches(".section-header-sticky, .section-header-sticky-always") &&
          !legacy.querySelector('sticky-header[disabled="true"]');
        const header = bar || (legacyEnabled ? legacy : null);
        const rect = header?.getBoundingClientRect();
        const bottom = rect?.width && rect.height && rect.top < window.innerHeight
          ? Math.max(0, Math.min(rect.bottom, window.innerHeight)) : 0;
        const top = bottom + 20;
        // Read every dimension before changing styles; writing top before reading the
        // gallery height forced a second layout on each scroll frame.
        // The grid item is stretched to the row (column balance, cq-product-page.css), so the element that pins and
        // is measured is the inner wrapper that holds the photo and thumbnails.
        const pinned = this.galleryWrap.querySelector(".product-media-wrapper") || this.galleryWrap;
        const sticky = pinned.getBoundingClientRect().height <= window.innerHeight - top - 20;

        if (top !== this.galleryTop) {
          this.galleryWrap.style.setProperty("--cq-gallery-top", `${top}px`);
          this.galleryTop = top;
        }
        // Never pin a gallery whose lower media, controls or disclaimer would be hidden.
        if (sticky !== this.gallerySticky) {
          this.galleryWrap.classList.toggle("cq-gallery-sticky", sticky);
          this.gallerySticky = sticky;
        }
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.photoFrame);
        clearTimeout(this.photoTimer);
        this.photoFrame = this.photoTimer = null;
        this.photoTasks?.clear();
        this.productPage?.removeEventListener("cq:variant-change", this.variantMediaHandler);
        this.variantMediaHandler = null;
        window.removeEventListener("scroll", this.stickyGalleryHandler);
        window.removeEventListener("resize", this.stickyGalleryHandler);
        this.stickyGalleryObserver?.disconnect();
        this.galleryHeaderObserver?.disconnect();
        this.galleryHeader?.removeEventListener("transitionend", this.stickyGalleryHandler);
        cancelAnimationFrame(this.stickyGalleryFrame);
        this.stickyGalleryHandler = null;
        this.stickyGalleryFrame = null;
        this.intentEvents?.abort();
        this.intentEvents = null;
        this.restIntent?.abort();
        this.restIntent = null;
        this.templateEvents?.abort();
        this.templateEvents = null;
        cancelAnimationFrame(this.railFrame);
        this.railFrame = null;
        window.removeEventListener("load", this.photoLoadHandler);
        this.photoLoadHandler = null;
        if (this.photoIdleTask != null) {
          if (window.cancelIdleCallback) window.cancelIdleCallback(this.photoIdleTask);
          else window.clearTimeout(this.photoIdleTask);
          this.photoIdleTask = null;
        }
        this.photoResizeObserver?.disconnect();
        this.photoResizeObserver = null;
        if (this.carouselQuery) {
          if (this.carouselQuery.removeEventListener) this.carouselQuery.removeEventListener("change", this.carouselQueryHandler);
          else this.carouselQuery.removeListener?.(this.carouselQueryHandler);
        }
        this.carouselQuery = null;
        this.carouselQueryHandler = null;
        const flickity = this.flickity;
        this.flickity = null;
        if (flickity) {
          flickity.destroy();
          // destroy() leaves a running settle animation behind; stop it (see applyVariantPhotos).
          flickity.isAnimating = false;
        }
      }

      setActiveMedia(mediaId, prepend, instant = false) {
        if (!mediaId) return;
        const activeMainMedia = [...this.elements.mains].find((element) => {
          return element.classList.contains("is-active");
        });
        // v6: a photo asked for by id while still in its template (a caller outside the plan) is brought in first, so
        // the call does what it did when every photo was in the page.
        if (!this.mediaElement(this.elements.mains, mediaId)) this.materialize([mediaId]);
        const nextMainMedia = this.mediaElement(this.elements.mains, mediaId);
        if (!nextMainMedia) return;
        if (instant && activeMainMedia === nextMainMedia) return;
        // P1: the photo about to show starts loading now, first in line (it may still be lazy and off screen).
        this.prime(nextMainMedia, "high", instant);
        // Its thumbnail stands in until it arrives, so switching photos never shows an empty tile (AJ 2026-10-18 03:37).
        this.standIn(nextMainMedia);
        const index = this.flickity?.isActive ? this.flickity.cells.findIndex((cell) => cell.element === nextMainMedia) : -1;

        if (this.flickity?.isActive && index >= 0 && this.flickity.selectedIndex !== index) {
          this.flickity.select(index, false, instant === true || window.matchMedia("(prefers-reduced-motion: reduce)").matches);
        }

        this.elements.thumbnails.forEach((thumb) => {
          thumb.classList.remove("is-active");
          thumb.querySelector("button")?.setAttribute("aria-current", "false");
        });
        const thumbnail = this.mediaElement(this.elements.thumbnails, mediaId);

        activeMainMedia?.classList.remove("is-active");

        nextMainMedia.classList.add("is-active");
        thumbnail?.classList.add("is-active");
        thumbnail?.querySelector("button")?.setAttribute("aria-current", "true");
        if (instant) this.afterPhotoPaint("play", () => {
          if (nextMainMedia.classList.contains("is-active")) this.playActiveMedia(nextMainMedia);
        });
        else this.playActiveMedia(nextMainMedia);

        if (!thumbnail) {
          // The code below is for thumbnails, return early
          // if there are not visible (e.g. in the quick view drawer).
          return;
        }

        if (prepend) {
          thumbnail.parentElement.prepend(thumbnail);
        } else if (instant) {
          // During an option's photo swap applyVariantPhotos re-arranges the rail first and reveals the thumbnail
          // the frame after that, so nothing is queued from here.
          if (!this.changingVariant) {
            this.afterPhotoPaint("rail", () => {
              if (thumbnail.classList.contains("is-active")) this.revealThumbnail(thumbnail);
            });
          }
        } else {
          this.revealThumbnail(thumbnail);
        }
      }

      revealThumbnail(thumbnail) {
        const scrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth";
        const container = thumbnail.parentElement;
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        // r5: the PDP rail fades its edges; keep the active thumbnail clear of the fade.
        const edge = container.hasAttribute("data-cq-rail-track") ? 40 : 0;

        const isHorizontalScroll = container.scrollWidth > container.clientWidth;

        if (isHorizontalScroll) {
          if (thumbnailRect.left < containerRect.left + edge) {
            container.scrollBy({
              left: thumbnailRect.left - containerRect.left - edge,
              behavior: scrollBehavior,
            });
          } else if (thumbnailRect.right > containerRect.right - edge) {
            container.scrollBy({
              left: thumbnailRect.right - containerRect.right + edge,
              behavior: scrollBehavior,
            });
          }
        } else {
          if (thumbnailRect.top < containerRect.top) {
            container.scrollBy({
              top: thumbnailRect.top - containerRect.top,
              behavior: scrollBehavior,
            });
          } else if (thumbnailRect.bottom > containerRect.bottom) {
            container.scrollBy({
              top: thumbnailRect.bottom - containerRect.bottom,
              behavior: scrollBehavior,
            });
          }
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector("deferred-media");

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }
    },
  );
}

if (!customElements.get("product-thumbnail-navigation")) {
  customElements.define(
    "product-thumbnail-navigation",
    class ProductThumbnailNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.gallery = document.querySelector(`#${this.getAttribute("for")}`);
        this.elements = {
          gallery: this.gallery,
          scroller: this.gallery.querySelector(".product-media-scroller"),
          thumbnailWrapper: this.gallery.querySelector(".product-media-thumbnails"),
          thumbnails: this.gallery.querySelectorAll(".product-media-thumbnail"),
          buttonPrev: this.querySelector(".js-prev"),
          buttonNext: this.querySelector(".js-next"),
        };

        if (!this.elements.thumbnails.length) {
          return;
        }

        // Detect if scrolling is horizontal or vertical
        this.isHorizontal =
          this.elements.scroller.clientWidth < this.elements.thumbnailWrapper.scrollWidth;
        const isVertical =
          this.elements.scroller.clientHeight < this.elements.thumbnailWrapper.scrollHeight;

        // Add class to hide gradients when no overflow
        if (!this.isHorizontal && !isVertical) {
          this.elements.scroller.classList.add("no-overflow");
        } else {
          this.elements.scroller.classList.remove("no-overflow");
        }

        if (this.isHorizontal || isVertical) {
          this.classList.remove("hidden");

          this.elements.buttonPrev.addEventListener("click", (event) => {
            event.preventDefault();
            this.prev();
          });

          this.elements.buttonNext.addEventListener("click", (event) => {
            event.preventDefault();
            this.next();
          });

          this.elements.thumbnailWrapper.addEventListener("scroll", () => {
            this.buttonAvailability();
          });
        }
      }

      prev() {
        if (this.isHorizontal) {
          const scrollPos = this.elements.thumbnailWrapper.scrollLeft;
          this.elements.thumbnailWrapper.scroll(scrollPos - 300, 0);
        } else {
          const scrollPos = this.elements.thumbnailWrapper.scrollTop;
          this.elements.thumbnailWrapper.scroll(0, scrollPos - 300);
        }
      }

      next() {
        if (this.isHorizontal) {
          const scrollPos = this.elements.thumbnailWrapper.scrollLeft;
          this.elements.thumbnailWrapper.scroll(scrollPos + 300, 0);
        } else {
          const scrollPos = this.elements.thumbnailWrapper.scrollTop;
          this.elements.thumbnailWrapper.scroll(0, scrollPos + 300);
        }
      }

      buttonAvailability() {
        const el = this.elements.thumbnailWrapper;
        let hasBottomed, hasTopped;

        if (this.isHorizontal) {
          hasBottomed = el.scrollLeft + el.clientWidth >= el.scrollWidth - 30;
          hasTopped = el.scrollLeft <= 30;
        } else {
          hasBottomed = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
          hasTopped = el.scrollTop <= 30;
        }

        this.elements.buttonPrev.classList.remove("pill-nav-item-disabled");
        this.elements.buttonNext.classList.remove("pill-nav-item-disabled");

        if (hasBottomed) {
          this.elements.buttonNext.classList.add("pill-nav-item-disabled");
        }

        if (hasTopped) {
          this.elements.buttonPrev.classList.add("pill-nav-item-disabled");
        }
      }
    },
  );
}

if (!customElements.get("media-zoom")) {
  customElements.define(
    "media-zoom",
    class MediaZoom extends HTMLElement {
      constructor() {
        super();

        this.mode = this.getAttribute("data-mode");

        this.zoomWidth = parseInt(this.getAttribute("data-zoom-width"), 10) || 150;
        this.zoomHeight = parseInt(this.getAttribute("data-zoom-height"), 10) || 150;

        this.lastEvent = null;
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.innerHTML += `<div class="media-zoom-zoom-area"></div>`;
        this.original = this.querySelector("img");
        this.zoomArea = this.querySelector(".media-zoom-zoom-area");

        this.original.addEventListener("mouseenter", this.onMouseEnter);
        this.original.addEventListener("mousemove", this.onMouseMove);
        this.original.addEventListener("mouseleave", this.onMouseLeave);

        if (this.mode === "lightbox_zoom_hover") {
          this.original.addEventListener("click", this.onClick);
        }

        this.zoomArea.style.width = `${this.zoomWidth}px`;
        this.zoomArea.style.height = `${this.zoomHeight}px`;

        this.preview = document.createElement("div");
        this.preview.classList.add("media-zoom-preview-area", "color-background-1");
        this.preview.innerHTML = '<div class="spinner"></div>';
        document.body.appendChild(this.preview);
      }

      disconnectedCallback() {
        this.original.removeEventListener("mouseenter", this.onMouseEnter);
        this.original.removeEventListener("mousemove", this.onMouseMove);
        this.original.removeEventListener("mouseleave", this.onMouseLeave);
        document.body.removeChild(this.preview);
      }

      onMouseEnter() {
        this.zoomArea.style.display = "block";
        this.preview.style.display = "block";

        const previewSize = `${this.original.offsetWidth}px`;
        this.preview.style.width = previewSize;
        this.preview.style.height = previewSize;

        // Load larger image on first mouse enter
        if (!this.previewImg) {
          this.previewImg = new Image();
          this.previewImg.src = this.getAttribute("data-zoom");
          this.previewImg.onload = () => {
            const zoomScale = this.original.offsetWidth / this.zoomWidth;

            this.previewImg.style.width = `${this.original.offsetWidth * zoomScale}px`;
            this.previewImg.style.height = `${this.original.offsetHeight * zoomScale}px`;

            this.preview.innerHTML = "";
            this.preview.appendChild(this.previewImg);
            if (this.lastEvent) {
              this.original.dispatchEvent(new MouseEvent("mousemove", this.lastEvent));
            }
          };
        } else {
          // Set the correct preview image always on mouse enter
          const zoomScale = this.original.offsetWidth / this.zoomWidth;
          this.previewImg.style.width = `${this.original.offsetWidth * zoomScale}px`;
          this.previewImg.style.height = `${this.original.offsetHeight * zoomScale}px`;
        }
      }

      onMouseMove(e) {
        this.lastEvent = e;
        const { left, top, width, height } = this.original.getBoundingClientRect();

        const x = e.pageX - (window.pageXOffset + left);
        const y = e.pageY - (window.pageYOffset + top);

        const zoomX = Math.min(Math.max(x - this.zoomWidth / 2, 0), width - this.zoomWidth);
        const zoomY = Math.min(Math.max(y - this.zoomHeight / 2, 0), height - this.zoomHeight);

        this.zoomArea.style.left = `${zoomX}px`;
        this.zoomArea.style.top = `${zoomY}px`;

        this.preview.style.position = "fixed";
        this.preview.style.left = `${left + width + 50}px`;
        this.preview.style.top = `${top - 19}px`;

        if (this.previewImg && this.previewImg.complete) {
          const previewX = (zoomX / width) * this.previewImg.width;
          const previewY = (zoomY / height) * this.previewImg.height;

          this.previewImg.style.transform = `translate(${-previewX}px, ${-previewY}px)`;
        }
      }

      onMouseLeave() {
        this.zoomArea.style.display = "none";
        this.preview.style.display = "none";
      }

      onClick() {
        const sectionId = this.getAttribute("data-modal-id");
        const modal = document.querySelector(`#ProductModal-${sectionId}`);

        if (modal) {
          modal.show(this);
        }
      }
    },
  );
}
